/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the depot management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useParams } from 'react-router';

import { Theme, makeStyles } from '@material-ui/core/styles';

/**
 * Used to format text.
 */
import Typography from '@material-ui/core/Typography';

import { RequestState } from '@ngt/request-utilities';

import { Button, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

import { DateTime } from 'luxon';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

import { PatientContext, CollapsibleTable } from '@ngt/online-patient-management-ui';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';
import DrugDispensationModal from './Modals/DrugDispensationModal';
import { useDrugDispensations } from '../../store/modules/drugmanagement/drugdispensations';
import { useTimepoints } from '../../store/modules/drugmanagement/timepoints';
import { useDrugDispensation } from '../../store/modules/drugmanagement/drugdispensation';
import { useDrugShipmentDepotBatches } from '../../store/modules/drugmanagement/drugshipmentdepotbatches';
import { usePharmacy } from '../../store/modules/drugmanagement/pharmacy';
import PatientInformation from '../../components/PatientInformation/PatientInformation';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import { useDrugs } from '../../store/modules/drugmanagement/drugs';
import { usePatientDrugs } from '../../store/modules/drugmanagement/patientdrugs';
import PatientDrugsModal from './Modals/PatientDrugsModal';
import PatientDrugInformation from '../../components/PatientDrugInformation/PatientDrugInformation';
import PageLayout from 'src/components/Layout/PageLayout';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugDispensationsParams {
    institutionCode?: string;
    patientStudyNumber?: string;
}

interface IDrugDispensationsProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    heading: {
        marginTop: theme.spacing(4)
    },
    buttonSet: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugManagementPermissions = [Dtos.Permission.DrugManagementAdminister, Dtos.Permission.DrugManagementDispenseDrug];

/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const useDispensationColumns = (drugDispensations: Dtos.DrugDispensation[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DrugDispensation>> = [
            {
                Header: 'Date of Dispensation',
                accessor: 'dateDispensation',
                Cell: props => DateTime.fromISO(props.cell.value!).toFormat('dd/MM/yyyy'),
                minWidth: 155,
                maxWidth: 155
            },
            {
                id: 'timepoint',
                Header: 'Visit',
                accessor: dispensation => dispensation.timepoint.value,
                minWidth: 155,
                maxWidth: 155
            },
            {
                id: 'action',
                Header: 'Action',
                accessor: dispensation => `${dispensation.depotBatch.batch.drug.drugName} (${dispensation.drugUnits} ${dispensation.depotBatch.batch.drug.containerName}s) dispensed (Expires ${DateTime.fromISO(dispensation.depotBatch.batch.dateExpiration!).toFormat('dd/MM/yyyy')})`,
                minWidth: 155,
                maxWidth: 155
            },
            {
                id: 'voided',
                Header: 'Notes',
                accessor: dispensation => (
                    <div style={{ whiteSpace: "pre-wrap" }}>
                        {dispensation.voided !== true ? "" : `Dispensation Voided - ${dispensation.notes}`}
                    </div>
                ),
                minWidth: 155,
                maxWidth: 155
            },
        ]

        return cols;
    }, []);

    return columns;
};

const DrugDispensations: React.FunctionComponent<IDrugDispensationsProps> = () => {
    const classes = useStyles();

    const params = useParams<IDrugDispensationsParams>();

    const [patient, , , , ] = React.useContext(PatientContext);
    const [pharmacy, , , pharmacyActions] = usePharmacy();
    const [[canAdministerDrugManagement, canDispenseDrug], ] = useBctApiPermissions(drugManagementPermissions);

    const [drugDispensations, drugDispensationsLoadState, drugDispensationsActions] = useDrugDispensations();
    const [drugShipmentDepotBatches, drugShipmentDepotBatchesLoadState, drugShipmentDepotBatchesActions] = useDrugShipmentDepotBatches();
    const [drugDispensation, drugDispensationLoadState, , drugDispensationActions] = useDrugDispensation();
    const [drugs, drugLoadState, drugActions] = useDrugs();
    const [patientDrugs, patientDrugsLoadState, , patientDrugsActions] = usePatientDrugs();

    const [timepoints, timepointsLoadState, timepointsActions] = useTimepoints();

    const [modalOpen, setModalOpen] = React.useState(false);
    const [patientDrugsModalOpen, setPatientDrugsModalOpen] = React.useState(false);

    const [drugDispensationForm, setDrugDispensationForm] = React.useState<Dtos.DrugDispensation | null>(null);
    const [patientDrugsForm, setPatientDrugsForm] = React.useState<Dtos.PatientDrug[] | null>(null);

    React.useEffect(() => {
        patientDrugsActions.clear();

        if (patient?.id) {
            patientDrugsActions.loadByPatientId(patient.id);
        }
    }, [patientDrugsActions, patient]);

    React.useEffect(() => {
        if (patient &&
            patient.id) {
            drugDispensationsActions.loadByPatientId(patient.id);
        }

        return () => {
            drugDispensationsActions.clear();
        };
    }, [patient, drugDispensationsActions]);

    React.useEffect(() => {
        if (params.institutionCode) {
            pharmacyActions.loadByInstCode(params.institutionCode);
            drugShipmentDepotBatchesActions.loadByInstCode(params.institutionCode);
        }

        return () => {
            pharmacyActions.clear();
            drugShipmentDepotBatchesActions.clear();
        }
    }, [params.institutionCode, drugShipmentDepotBatchesActions, pharmacyActions]);

    React.useEffect(() => {
        if (patient) {
            const typedPatient = patient as Dtos.Patient;

            if (typedPatient &&
                typedPatient.treatmentId) {
                timepointsActions.loadByTreatmentId(typedPatient.treatmentId);
            }
        }
    }, [patient, timepointsActions]);

    React.useEffect(() => {
        drugActions.load();

        return () => {
            drugActions.clear();
        };
    }, [drugActions]);

    React.useEffect(() => {
        if (drugDispensationLoadState.state === RequestState.Success) {
            setDrugDispensationForm(drugDispensation);

            setModalOpen(true);
        }
    }, [drugDispensationLoadState.state, drugDispensation]);

    React.useEffect(() => {
        if (patientDrugsLoadState.state === RequestState.Success) {
            let patientDrugForm: Dtos.PatientDrug[] = [];

            if (patientDrugs &&
                patientDrugs.length > 0) {
                patientDrugForm.push(...patientDrugs);
            }

            if (drugs &&
                patientDrugForm.length < drugs.length) {
                drugs
                    .filter(d => !patientDrugForm.some(pdf => pdf.drugId === d.id))
                    .forEach(d => {
                        const patientDrug: Dtos.PatientDrug = new Dtos.PatientDrug({
                            patientId: patient?.id,
                            drugId: d.id,
                            patientTreatmentStatus: Dtos.PatientTreatmentStatus.OffTreatment
                        });

                        patientDrugForm.push(patientDrug);
                    });
            }

            patientDrugForm.sort((a, b) => a.drugId! < b.drugId! ? -1 : 1);

            setPatientDrugsForm(patientDrugForm);
            //setPatientDrugsModalOpen(true);
        }
    }, [patientDrugs, patientDrugsLoadState, drugs, setPatientDrugsForm, patient, setPatientDrugsModalOpen]);

    const onFormSave = React.useCallback(() => {
        if (patient &&
            patient.id) {
            drugDispensationActions.clear();
            drugDispensationsActions.loadByPatientId(patient.id);
            patientDrugsActions.loadByPatientId(patient.id);
        }
    }, [patient, drugDispensationActions, drugDispensationsActions, patientDrugsActions]);

    const onPatientDrugsStatusFormSave = React.useCallback(() => {
        setPatientDrugsForm(null);
        patientDrugsActions.clear();

        if (patient?.id) {
            patientDrugsActions.loadByPatientId(patient.id);
        }
    }, [setPatientDrugsForm, patientDrugsActions, patient]);

    const addDispensationClick = React.useCallback(() => {
        setDrugDispensationForm(new Dtos.DrugDispensation({
            patientId: patient?.id,
            pharmacyId: pharmacy?.id
        }));

        setModalOpen(true);
    }, [setModalOpen, patient, pharmacy]);

    const editPatientTreatmentStatusClick = React.useCallback(() => {
        //if (patient &&
        //    patient.id) {
        //    patientDrugsActions.clear();
        //    patientDrugsActions.loadByPatientId(patient.id);
        //}

        setPatientDrugsModalOpen(true);
    }, [patient, patientDrugsActions, setPatientDrugsModalOpen]);

    const onRowClick = React.useCallback((row: Row<Dtos.DrugDispensation>) => {
        if (!!canDispenseDrug &&
            row &&
            row.original) {
            drugDispensationActions.clear();
            drugDispensationActions.loadById(row.original.id!);
        }
    }, [canDispenseDrug, drugDispensationActions]);

    const dispensationColumns = useDispensationColumns(drugDispensations);

    //this is here for general cleanup in case something is loaded into redux and never reset
    React.useEffect(() => {
        return () => {
            setPatientDrugsForm(null);
            patientDrugsActions.clear();
            drugDispensationActions.clear();
            timepointsActions.clear();
        }
    }, [patientDrugsActions, drugDispensationActions, timepointsActions]);

    return (
        <>
            <DrugDispensationModal
                drugDispensation={drugDispensationForm}
                timepoints={timepoints}
                timepointsLoadState={timepointsLoadState}
                drugShipmentDepotBatches={drugShipmentDepotBatches}
                drugShipmentDepotBatchesLoadState={drugShipmentDepotBatchesLoadState}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
            />
            <PatientDrugsModal
                patientDrugs={patientDrugsForm}
                patientDrugsLoadState={patientDrugsLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                modalOpen={patientDrugsModalOpen}
                setModalOpen={setPatientDrugsModalOpen}
                onFormSave={onPatientDrugsStatusFormSave}
            />
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                        institutionCode={pharmacy?.institutionCode}
                        institutionName={pharmacy?.pharmacyName}
                        showInstitution={true}
                    />
                }
                heading="Drug Dispensations"
            >
                <Paper
                    className={classes.container}
                >
                    <PatientDrugInformation
                        patient={patient}
                    />
                    <div
                        className={classes.buttonSet}
                    >
                        {
                            !!canAdministerDrugManagement && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={editPatientTreatmentStatusClick}
                                    className={classes.button}
                                    disabled={!patient || !patient.id || !drugs}
                                >
                                    Edit Patient Treatment Status
                                </Button>
                            )
                        }
                        {
                            !!canDispenseDrug && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.button}
                                    onClick={addDispensationClick}
                                >
                                    Add Dispensation
                                </Button>
                            )
                        }
                    </div>
                    <CollapsibleTable
                        title="Dispensation"
                        loadState={drugDispensationsLoadState}
                        data={drugDispensations}
                        columns={dispensationColumns}
                        onRowClick={onRowClick}
                    />
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DrugDispensations;